import { useState, useEffect, useRef } from 'react';
import { money, originBase, shareLinkText } from './util';
import FiltroTipoLote, { filtrarPorTipo, tipoInicial } from '../components/FiltroTipoLote';
import LinkGenerado from '../components/LinkGenerado';
import BotonGenerarLink from '../components/BotonGenerarLink';

const API = process.env.REACT_APP_API_URL || '';

// El dueño genera links de venta igual que un RRPP: elige lote + cantidad,
// genera un link compartible y se lo manda a quien le pidió la entrada.
export default function GenerarEntradas({ event, api, onClose }) {
  // Los lotes de boletería se venden SOLO en la caja de boletería (su TPV): no se generan acá.
  const lots = (event.lots || []).filter(l => l.status !== 'sold_out' && l.status !== 'paused'
    && !(Array.isArray(l.channels) && l.channels.includes('boleteria')));
  // El filtro arranca en Ingresos, así que el lote inicial tiene que ser uno de ESE
  // grupo: si no, abre con un combo elegido que la lista no muestra.
  const [filtroTipo, setFiltroTipo] = useState(() => tipoInicial(lots));
  const [lotId, setLotId] = useState(() => filtrarPorTipo(lots, tipoInicial(lots))[0]?.id || '');
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [coupon, setCoupon] = useState('');
  const [couponInfo, setCouponInfo] = useState(null);
  const [justCreated, setJustCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  // Mientras el servidor contesta: el botón se pone gris y se va llenando, y no acepta
  // otro toque. Sin esto, el segundo que tarda en abrir la ventana del link terminaba
  // en dos links generados.
  const [generando, setGenerando] = useState(false);
  const generandoRef = useRef(false);

  // LA PROMO LA CALCULA EL SERVIDOR, con la misma función que usa al generar el link.
  // La pantalla no hace su propia cuenta de la promo: así fue como mostraba $44.000 por
  // un link de 4 × $11.000 con 4x3 que se cobraba y se rendía en $33.000.
  const [cot, setCot] = useState(null);
  useEffect(() => {
    if (!lotId) { setCot(null); return; }
    let vivo = true;
    // Un respiro antes de preguntar: tocando "+" cuatro veces no hacen falta cuatro
    // consultas. Y `vivo` descarta la respuesta de una cantidad que ya no es la actual.
    const t = setTimeout(() => {
      api.get(`/api/rrpp-links/cotizar?event_id=${event.id}&lot_id=${lotId}&max_uses=${qty}`)
        .then(c => { if (vivo) setCot(c); })
        .catch(() => { if (vivo) setCot(null); });
    }, 200);
    return () => { vivo = false; clearTimeout(t); };
  }, [lotId, qty, event.id]); // eslint-disable-line

  const lot = lots.find(l => l.id === lotId);
  const unit = lot ? Number(lot.price) : 0;
  const discPerTicket = !couponInfo?.valid ? 0
    : (couponInfo.type === 'fixed' ? Math.min(unit, Number(couponInfo.amount) || 0) : Math.round(unit * (Number(couponInfo.percent) || 0) / 100));
  // Solo se usa la cotización si es de ESTA cantidad: mientras llega la nueva, no se
  // muestra el descuento de la anterior.
  const promoMonto = cot && cot.qty === qty && cot.promo ? Number(cot.promo.monto) : 0;
  // El cupón vale por UNA entrada del link: la primera con descuento, el resto al precio
  // normal. La promo se resta aparte: los dos beneficios se suman.
  const valor = Math.max(0, Math.max(0, unit - discPerTicket) + unit * Math.max(0, qty - 1) - promoMonto);
  const valorSinDesc = unit * qty;
  const hayDescuento = discPerTicket > 0 || promoMonto > 0;

  function selectLot(id) { setLotId(id); setCouponInfo(null); }

  // Al cambiar el filtro hay que soltar el lote elegido si dejó de estar a la vista: si
  // no, se generaría un link de un lote que la pantalla ya no muestra.
  const lotesVisibles = filtrarPorTipo(lots, filtroTipo);
  function cambiarFiltro(k) {
    setFiltroTipo(k);
    const visibles = filtrarPorTipo(lots, k);
    if (!visibles.some(l => l.id === lotId)) selectLot(visibles[0]?.id || '');
  }

  async function validateCoupon() {
    if (!coupon.trim()) { setCouponInfo(null); return; }
    if (!lotId) { setCouponInfo({ valid: false, error: 'Elegí primero el lote' }); return; }
    try {
      const r = await fetch(`${API}/api/public/coupon/${coupon.trim().toUpperCase()}?event_id=${event.id}&lot_id=${lotId}`);
      const d = await r.json();
      setCouponInfo(r.ok ? d : { valid: false, error: d.error });
    } catch { setCouponInfo({ valid: false, error: 'Error' }); }
  }

  function linkUrl(code) { return `${originBase()}/l/${code}`; }
  function copy(code) { navigator.clipboard?.writeText(linkUrl(code)); setCopied(true); setTimeout(() => setCopied(false), 1800); }
  async function share(link) {
    const url = linkUrl(link.code);
    const text = shareLinkText({ eventName: event.name, eventDate: event.date, cantidad: link.max_uses, lote: link.lot_name });
    if (navigator.share) { try { await navigator.share({ title: event.name, text, url }); } catch (e) {} }
    else { try { await navigator.clipboard?.writeText(`${text}\n${url}`); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {} }
  }

  async function generate() {
    // Igual que en el panel del RRPP: el ref frena los dos toques del mismo instante.
    if (!lotId || generandoRef.current) return;
    generandoRef.current = true;
    setGenerando(true);
    try {
      const created = await api.post('/api/rrpp-links', {
        event_id: event.id, lot_id: lotId, max_uses: qty, note: note.trim() || null,
        coupon_code: couponInfo?.valid ? coupon.trim().toUpperCase() : null,
      });
      // lot_price viaja para que la ventana del link muestre su valor con linkValue(), la
      // misma cuenta que usan la administración y la rendición.
      setJustCreated({ ...created, lot_name: lot?.name, lot_price: lot?.price });
      setNote(''); setCopied(false);
    } catch (e) { alert(e.message); }
    finally { generandoRef.current = false; setGenerando(false); }
  }
  function resetForm() { setQty(1); setNote(''); setCoupon(''); setCouponInfo(null); setJustCreated(null); setCopied(false); }

  return (
    <div className="lb-modal-backdrop" onClick={onClose}>
      <div className="lb-modal lb-modal--wide" onClick={e => e.stopPropagation()}>
        <div style={{ padding: '24px 28px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div className="lb-eyebrow">Generar link de venta</div>
            <div className="lb-h" style={{ fontSize: 22, marginTop: 4 }}>{event.name}</div>
          </div>
          <button className="lb-iconbtn" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '24px 28px' }}>
          {/* generador */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 460 }}>
            <Field label="Tipo de entrada / lote">
              <FiltroTipoLote lotes={lots} valor={filtroTipo} onChange={cambiarFiltro} />
              <select className="lb-select" value={lotId} onChange={e => selectLot(e.target.value)}>
                {lotesVisibles.length === 0 && <option value="">Sin lotes disponibles</option>}
                {lotesVisibles.map(l => <option key={l.id} value={l.id}>{l.name} · {money(l.price)}</option>)}
              </select>
            </Field>
            <Field label="Cantidad">
              <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid var(--border)', borderRadius: 999 }}>
                <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ border: 0 }} onClick={() => setQty(q => Math.max(1, q - 1))}>–</button>
                <span style={{ minWidth: 50, textAlign: 'center', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 18 }}>{qty}</span>
                <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ border: 0 }} onClick={() => setQty(q => q + 1)}>+</button>
              </div>
            </Field>
            <Field label="Nota · para quién (opcional)">
              <input className="lb-input" value={note} onChange={e => setNote(e.target.value)} placeholder="Ej: Juan de Funes" />
            </Field>
            <Field label="Cupón de descuento (opcional)">
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="lb-input" value={coupon} onChange={e => { setCoupon(e.target.value.toUpperCase()); setCouponInfo(null); }} placeholder="Ej: JUNIO20" />
                <button type="button" className="lb-btn lb-btn--ghost lb-btn--sm" onClick={validateCoupon} disabled={!coupon.trim()}>Aplicar</button>
              </div>
              {couponInfo && (couponInfo.valid
                ? <div style={{ fontSize: 12, color: '#2ecc71', marginTop: 6 }}>✓ {couponInfo.type === 'fixed' ? `${money(couponInfo.amount)} de descuento` : `${couponInfo.percent}% de descuento`}{couponInfo.name ? ` · ${couponInfo.name}` : ''} · se aplica a 1 entrada del link</div>
                : <div style={{ fontSize: 12, color: 'var(--red-300)', marginTop: 6 }}>{couponInfo.error}</div>)}
            </Field>
            <div style={{ padding: '14px 16px', background: 'var(--ink-050)', border: '1px solid ' + (promoMonto > 0 ? 'rgba(46,204,113,0.45)' : 'var(--border)'), borderRadius: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="lb-eyebrow">Total{hayDescuento ? ' con descuento' : ''}</span>
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  {hayDescuento && <span style={{ fontSize: 14, color: 'var(--ink-600)', textDecoration: 'line-through' }}>{money(valorSinDesc)}</span>}
                  <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22, color: '#fff' }}>{money(valor)}</span>
                </span>
              </div>
              {/* Es lo que se cobra y lo que se rinde por este link: el mismo número que
                  después aparece en la administración y en la rendición. */}
              {promoMonto > 0 && (
                <div style={{ fontSize: 12.5, color: '#2ecc71', marginTop: 8 }}>
                  ✓ Promoción aplicada · {cot.promo.detalle} · −{money(promoMonto)}
                </div>
              )}
            </div>
            {/* Bloqueado mientras genera y mientras hay un link recién generado: el
                link se muestra ENCIMA y para hacer otro hay que tocar "Generar otro". */}
            <BotonGenerarLink generando={generando} hecho={!!justCreated} disabled={!lotId} onClick={generate} />

            <LinkGenerado
              link={justCreated}
              url={justCreated ? linkUrl(justCreated.code) : ''}
              copiado={copied}
              onCopiar={() => copy(justCreated.code)}
              onCompartir={() => share(justCreated)}
              onOtro={resetForm}
              onCerrar={resetForm}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="lb-field"><div className="lb-label">{label}</div>{children}</div>;
}
